import { ReactNode } from 'react';

import { generateRandomString } from 'utils';
import { Notification, NotificationType, useNotification } from 'containers/Notifications';

export type Toast = Notification & {};

export type ToastId = string;

export type ToastContent = string | ReactNode;

export type ToastOptions = { id: ToastId; autoDismiss?: boolean };

export type ToastPromiseParams = {
  loading?: string;
  success?: string;
  error?: string;
};

export type UseToastResult = {
  toasts: Toast[];
  dismiss(id: ToastId): void;
  info(content: ToastContent, options?: ToastOptions): void;
  warn(content: ToastContent, options?: ToastOptions): void;
  success(content: ToastContent, options?: ToastOptions): void;
  error(content: ToastContent, options?: ToastOptions): void;
  loading(content: ToastContent, options?: ToastOptions): void;
  promise<T>(promise: Promise<T>, params: ToastPromiseParams, options?: ToastOptions): Promise<T>;
  confirm(content: ToastContent, options?: ToastOptions): void;
};

const useToast = (): UseToastResult => {
  const notification = useNotification();

  const dismiss = (id: ToastId) => {
    notification.close(id);
  };

  const info = (content: ToastContent, options?: ToastOptions) => {
    notification.show({
      type: NotificationType.INFO,
      id: options?.id?.toString() || generateRandomString(),
      content,
      options: { autoDismiss: options?.autoDismiss }
    });
  };

  const warn = (content: ToastContent, options?: ToastOptions) => {
    notification.show({
      type: NotificationType.WARN,
      id: options?.id?.toString() || generateRandomString(),
      content,
      options: { autoDismiss: options?.autoDismiss }
    });
  };

  const success = (content: ToastContent, options?: ToastOptions) => {
    notification.show({
      type: NotificationType.SUCCESS,
      id: options?.id?.toString() || generateRandomString(),
      content,
      options: { autoDismiss: options?.autoDismiss }
    });
  };

  const error = (content: ToastContent, options?: ToastOptions) => {
    notification.show({
      type: NotificationType.ERROR,
      id: options?.id?.toString() || generateRandomString(),
      content,
      options: { autoDismiss: options?.autoDismiss }
    });
  };

  const loading = (content: ToastContent, options?: ToastOptions) => {
    const n = {
      type: NotificationType.LOADING,
      id: options?.id?.toString() || generateRandomString(),
      content,
      options: {
        autoDismiss: false,
        dismissOnClick: false,
        duration: Infinity
      }
    };

    notification.show(n);
  };

  const promise = <T>(content: Promise<T>, params: ToastPromiseParams, options?: ToastOptions): Promise<T> => {
    const id = options?.id?.toString() || generateRandomString();

    loading(params.loading || 'Loading', { ...options, id });

    return new Promise((resolve, reject) => {
      content
        .then(res => {
          success(params.success || 'Success', { ...options, id });
          resolve(res);
        })
        .catch(e => {
          error(params.error || e.message || 'Failed', { ...options, id });
          reject(e);
        });
    });
  };

  const confirm = (content: ToastContent, options?: ToastOptions) => {
    notification.show({
      type: NotificationType.CUSTOM,
      id: options?.id?.toString() || generateRandomString(),
      content,
      options: {
        autoDismiss: false,
        dismissOnClick: false,
        duration: Infinity
      }
    });
  };

  return {
    toasts: notification.notifications,
    dismiss,
    info,
    warn,
    error,
    success,
    loading,
    promise,
    confirm
  };
};

export default useToast;
